import { ChevronDown, ChevronRight } from '@carbon/icons-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ColumnDef, Row, createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { CalendarIcon } from 'lucide-react';
import React from 'react';

import { TableBody, TableHeader, TableProvider } from 'DesignSystem/features/TableContext';
import { Table, TableCell, TableRow } from 'DesignSystem/ui/table';
import { prefCardCasesInfiniteQuery, prefCardItemDetailsQuery } from 'pages/preference-cards/api';
import { CardConflictDifferenceType, PrefCardCase } from 'pages/preference-cards/types';
import { formatDate } from 'utils/helpers';

type DateGroup = {
  dateKey: string;
  displayDate: string;
  cases: PrefCardCase[];
  isCollapsed: boolean;
};

type TableRow = PrefCardCase | DateGroup;

type CasesTableProps = {
  cardId: string;
  itemId: string;
  differenceType?: CardConflictDifferenceType | undefined;
  canFetchItemDetails?: boolean;
};

export const CasesTable = ({ cardId, itemId, differenceType, canFetchItemDetails = true }: CasesTableProps) => {
  const { data: item } = useQuery(
    prefCardItemDetailsQuery(cardId, itemId, canFetchItemDetails && differenceType !== 'ADD')
  );
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery(
    prefCardCasesInfiniteQuery(cardId, itemId)
  );

  const [expandedDateGroups, setExpandedDateGroups] = React.useState<string[]>([]);

  const toggleDateGroup = React.useCallback(
    (dateGroup: string) => () => {
      setExpandedDateGroups((prev) => {
        const isExpanded = prev.includes(dateGroup);

        if (isExpanded) {
          return prev.filter((id) => id !== dateGroup);
        } else {
          return [...prev, dateGroup];
        }
      });
    },
    []
  );

  const groupedCases = React.useMemo(() => {
    const allCases = data?.pages?.flatMap((page) => page.items) ?? [];
    const baselineDate = item?.recommendation_impact_start_date;

    // Only group if we have a valid baseline date
    if (!baselineDate) {
      return allCases;
    }

    return groupCasesByDate(allCases, baselineDate);
  }, [data, item]);

  const nonEmptyGroups = React.useMemo(() => {
    return groupedCases.filter((item) => {
      if (isDateGroup(item)) return item.cases.length > 0;

      return true;
    });
  }, [groupedCases]);

  const hasAccordions = nonEmptyGroups.some((item) => isDateGroup(item));
  const allAccordionsExpanded =
    !hasAccordions ||
    nonEmptyGroups.filter((item) => isDateGroup(item)).every((item) => expandedDateGroups.includes(item.dateKey));

  const tableContainerRef = React.useRef<HTMLDivElement>(null);

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

        // Check if user is within 200px of the bottom
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 200;

        //once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
        if (isNearBottom && hasNextPage && !isFetchingNextPage && allAccordionsExpanded) {
          void fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage, allAccordionsExpanded]
  );

  React.useEffect(
    function fetchMoreOnScroll() {
      const containerElement = tableContainerRef.current;
      if (!containerElement) return;

      // Initial mount fetch
      fetchMoreOnBottomReached(containerElement);

      const handleScroll = () => {
        fetchMoreOnBottomReached(containerElement);
      };

      containerElement.addEventListener('scroll', handleScroll);

      return () => {
        containerElement.removeEventListener('scroll', handleScroll);
      };
    },
    [fetchMoreOnBottomReached]
  );

  const flattenedData = React.useMemo(() => {
    return nonEmptyGroups.reduce<TableRow[]>((result, item) => {
      if (isDateGroup(item)) {
        result.push(item);

        // If expanded, add the individual cases
        if (expandedDateGroups.includes(item.dateKey)) {
          result.push(...item.cases);
        }
      } else {
        result.push(item);
      }

      return result;
    }, []);
  }, [nonEmptyGroups, expandedDateGroups]);

  const table = useReactTable({
    data: flattenedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => (isDateGroup(row) ? row.dateKey : row.case_id),
  });

  return (
    <div className="w-full flex-1 overflow-y-auto">
      <div className="flex h-full min-h-0 flex-col overflow-hidden">
        <TableProvider table={table} isLoading={isLoading}>
          <div className="min-h-0 grow-0">
            <Table containerClassName="max-h-[500px]" containerRef={tableContainerRef}>
              <caption className="sr-only">Cases table showing procedure cases with date-based grouping</caption>
              <TableHeader />
              <TableBody
                renderRow={(row: Row<TableRow>) => {
                  const item = row.original;

                  return (
                    <React.Fragment key={row.id}>
                      {isDateGroup(item) ? (
                        <TableRowDateGroup
                          key={row.id}
                          item={item}
                          expanded={expandedDateGroups.includes(item.dateKey)}
                          onToggle={toggleDateGroup(item.dateKey)}
                        />
                      ) : (
                        <TableRow key={row.id}>
                          {row.getVisibleCells().map((cell) => {
                            return (
                              <TableCell key={cell.id} className="px-3">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                }}
              />
            </Table>
          </div>
        </TableProvider>
      </div>
    </div>
  );
};

const TableRowDateGroup = ({
  item,
  expanded,
  onToggle,
}: {
  item: DateGroup;
  expanded: boolean;
  onToggle: () => void;
}) => {
  return (
    <TableRow className="bg-[#EEF2FF] hover:bg-[#ebeefc]">
      <TableCell colSpan={6} className="h-auto p-0">
        <button
          className="type-body-xs flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-2 font-medium"
          onClick={onToggle}
          role="button"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${item.displayDate} group containing ${item.cases.length} cases`}
          aria-describedby={`${item.dateKey}-description`}
        >
          <div className="flex items-center gap-1 text-zinc-800">
            <CalendarIcon className="h-4 w-4" />
            <span>
              Item edited on this preference card {item.displayDate}. Recommendations not influenced prior to this date.
            </span>
          </div>
          <div className="flex items-center gap-1 text-[#4F46E5]">
            <span id={`${item.dateKey}-description`}>{expanded ? 'Hide' : 'Show'} prior cases</span>
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </div>
        </button>
      </TableCell>
    </TableRow>
  );
};

function groupCasesByDate(cases: PrefCardCase[], baselineDate: string): TableRow[] {
  const baseline = new Date(baselineDate);

  // Separate cases by date relationship to baseline
  const { beforeBaseline, afterBaseline, noDate } = cases.reduce<{
    beforeBaseline: PrefCardCase[];
    afterBaseline: PrefCardCase[];
    noDate: PrefCardCase[];
  }>(
    (acc, caseItem) => {
      if (!caseItem.case_date) {
        acc.noDate.push(caseItem);
      } else {
        const caseDate = new Date(caseItem.case_date);
        if (caseDate <= baseline) {
          acc.beforeBaseline.push(caseItem);
        } else {
          acc.afterBaseline.push(caseItem);
        }
      }
      return acc;
    },
    {
      beforeBaseline: [],
      afterBaseline: [],
      noDate: [],
    }
  );

  // Sort cases by date (most recent first)
  const sortByDate = (a: PrefCardCase, b: PrefCardCase) => {
    if (!a.case_date || !b.case_date) return 0;
    return new Date(b.case_date).getTime() - new Date(a.case_date).getTime();
  };

  const sortedAfterBaseline = afterBaseline.sort(sortByDate);
  const sortedBeforeBaseline = beforeBaseline.sort(sortByDate);
  const sortedNoDate = noDate.sort(sortByDate);

  // Create date groups for cases before baseline
  const dateGroups: DateGroup[] =
    beforeBaseline.length > 0
      ? [
          {
            dateKey: 'before-baseline',
            displayDate: formatDate(baselineDate, '2-digit', '2-digit', '2-digit'),
            cases: sortedBeforeBaseline,
            isCollapsed: true,
          },
        ]
      : [];

  // Return individual cases first (most recent), then grouped cases
  return [...sortedAfterBaseline, ...sortedNoDate, ...dateGroups];
}

const isDateGroup = (row: TableRow): row is DateGroup => 'dateKey' in row;

const columnHelper = createColumnHelper<TableRow>();
const columns: ColumnDef<TableRow, any>[] = [
  columnHelper.accessor('case_date', {
    header: 'Procedure Date',
    cell: ({ row }) => {
      const item = row.original;
      if (isDateGroup(item)) return null;
      return item.case_date ? formatDate(item.case_date, '2-digit', '2-digit', '2-digit') : '–';
    },
  }),
  columnHelper.accessor('client_case_id', {
    header: 'ID',
    cell: ({ row }) => {
      const item = row.original;
      if (isDateGroup(item)) return null;
      return item.client_case_id;
    },
  }),
  columnHelper.accessor('first_physician_name', {
    header: 'Primary Physician',
    cell: ({ row }) => {
      const item = row.original;
      if (isDateGroup(item)) return null;
      return item.first_physician_name ?? '–';
    },
  }),
  columnHelper.accessor('location_name', {
    header: 'Approved OR Location',
    cell: ({ row }) => {
      const item = row.original;
      if (isDateGroup(item)) return null;
      return item.location_name ?? '–';
    },
  }),
  columnHelper.accessor('total_items_used', {
    header: 'Used',
    cell: ({ row }) => {
      const item = row.original;
      if (isDateGroup(item)) return null;
      return item.total_items_used ?? '–';
    },
  }),
  columnHelper.accessor('total_items_wasted', {
    header: 'Wasted',
    cell: ({ row }) => {
      const item = row.original;
      if (isDateGroup(item)) return null;
      return item.total_items_wasted ?? '–';
    },
  }),
];
